import { useRef, useState } from 'react';

/**
 * Turn any container into a drop target for files.
 *
 *   const [dragging, dropHandlers] = useFileDrop(handleFiles);
 *   <div {...dropHandlers} className={dragging ? 'is-over' : ''}>
 *
 * Two details matter. Dragging selected text or a link must not light the zone
 * up, hence the `Files` check; and `dragleave` fires every time the cursor
 * crosses a *child* element, so a naive handler makes the zone flicker and drop
 * out from under the user — the depth counter is what keeps it lit.
 */
export default function useFileDrop(onFiles) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);

  const carriesFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes('Files');

  const handlers = {
    onDragEnter: (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setDragging(true);
    },
    onDragOver: (e) => {
      // Without preventDefault the browser navigates to the dropped file.
      if (carriesFiles(e)) e.preventDefault();
    },
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      onFiles(Array.from(e.dataTransfer.files || []));
    },
  };

  return [dragging, handlers];
}

/** A phone exposes its camera through `capture` on a file input; a mouse does not. */
export function isTouchDevice() {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(pointer: coarse)').matches
  );
}
